import * as React from "react";

import {
  Button,
  Classes,
  FormGroup,
  HTMLSelect,
  Icon,
  InputGroup,
  Overlay,
  Spinner,
  TextArea
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import RMAProductSelector, {
  IRMAOrderItem
} from "~/components/RMAForm/RMAProductSelector";
import { REASONS_REQUIRING_FILE_UPLOAD } from "~/config/support_reasons";
import { EMAIL_REGEX } from "~/constants";
import { ITrengoTicket } from "~/containers/RMAForm/RMAFormContainer";
import { OrderNumberValidationState } from "~/graphql/mutations/trengoTicket/createTrengoTicket";
import { IOrder as INexusOrder } from "~/graphql/queries/order/orderByName";
import { IOrder } from "~/graphql/queries/order/orderByNumberAndPostalCode";
import isNexusPlatform from "~/helpers/isNexusPlatform";
import { shopConfig } from "~/helpers/shopConfig";
import { StyledCallout as Callout } from "~/layouts/StyledCallout";
import { StyledCard as Card } from "~/layouts/StyledCard";
import NexusRMAProductSelector from "./NexusRMAProductSelector";

const RightAligned = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

interface IProps extends WithTranslation {
  loading?: boolean;
  order: IOrder | undefined;
  nexusOrder: INexusOrder | undefined;
  errorCode?: string;
  postalCode: string;
  trengoTicket: ITrengoTicket;
  orderNumberValidationState: OrderNumberValidationState;
  rmaOrderItems: IRMAOrderItem[];
  onProductSelectorChange: (rmaOrderItems: IRMAOrderItem[]) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onChange: (changes: Partial<ITrengoTicket>) => void;
  onValidateOrderNumber: () => void;
  onPostalCodeChange: (postalCode: string) => void;
}

type Topic = [string, string];

class RMAForm extends React.Component<IProps> {
  public render() {
    const {
      t,
      loading = false,
      trengoTicket: {
        name,
        email,
        phone,
        orderNumber,
        description,
        reason,
        document
      },
      postalCode,
      nexusOrder,
      order,
      rmaOrderItems,
      orderNumberValidationState,
      onProductSelectorChange
    } = this.props;

    const orderNumberValid =
      orderNumberValidationState === OrderNumberValidationState.VALID;
    const orderNumberInvalid =
      orderNumber &&
      postalCode &&
      orderNumberValidationState === OrderNumberValidationState.INVALID;
    const fileUploadRequired = REASONS_REQUIRING_FILE_UPLOAD.includes(reason);

    const { webshop } = shopConfig;
    const allowedBuyersProtection = [
      "grandado-glo",
      "grandado-deu",
      "grandado-aus",
      "fi-grandado",
      "fr-grandado",
      "nl-grandado"
    ];

    const topics: Topic[] = (t("inputs.subject.options", {
      returnObjects: true
    }) as Topic[]).filter(([key]) => {
      if (
        key === "Buyers Protection" &&
        !allowedBuyersProtection.includes(webshop)
      ) {
        return false;
      }
      return true;
    });

    const emailValid = email === "" || EMAIL_REGEX.test(email);

    return (
      <>
        {this.renderError()}

        <Card>
          <form onSubmit={this.handleSubmit} method="post">
            <Overlay isOpen={loading}>
              <Center className={Classes.DARK}>
                <Spinner />
              </Center>
            </Overlay>

            <FormGroup label={t("inputs.name.label")} labelInfo="*">
              <InputGroup
                onChange={this.handleChange("name")}
                value={name}
                autoComplete="name"
              />
            </FormGroup>

            <FormGroup
              label={t("inputs.email.label")}
              labelInfo="*"
              helperText={
                !emailValid
                  ? t("inputs.email.error")
                  : t("inputs.email.helperText")
              }
            >
              <InputGroup
                onChange={this.handleChange("email")}
                value={email}
                type="email"
                placeholder="email@example.com"
                autoComplete="email"
                intent={!emailValid ? "danger" : "none"}
              />
            </FormGroup>

            <FormGroup label={t("inputs.phone.label")} labelInfo="*">
              <InputGroup
                onChange={this.handleChange("phone")}
                value={phone}
                type="phone"
                autoComplete="phone"
              />
            </FormGroup>

            <FormGroup label={t("inputs.postalCode.label")} labelInfo="*">
              <InputGroup
                onBlur={this.handleOrderNumberBlur}
                onChange={this.handlePostalCodeChange}
                value={postalCode}
              />
            </FormGroup>

            <FormGroup
              label={t("inputs.orderNumber.label")}
              labelInfo="*"
              helperText={
                orderNumberInvalid ? (
                  <Trans i18nKey="rmaForm:inputs.orderNumber.errors.invalid">
                    The entered order number wasn't found in our system. If you
                    don't know your order number, you can{" "}
                    <Link to="/faq/orders">read here how to find it</Link>
                  </Trans>
                ) : (
                  t("inputs.orderNumber.helperText")
                )
              }
              intent={orderNumberInvalid ? "danger" : undefined}
            >
              <InputGroup
                onBlur={this.handleOrderNumberBlur}
                onChange={this.handleChange("orderNumber")}
                value={orderNumber}
                {...this.orderNumberValidationStateProps()}
              />
            </FormGroup>

            {orderNumberValid &&
              (isNexusPlatform
                ? nexusOrder && (
                    <NexusRMAProductSelector
                      nexusOrder={nexusOrder}
                      rmaOrderItems={rmaOrderItems}
                      onChange={onProductSelectorChange}
                    />
                  )
                : order && (
                    <RMAProductSelector
                      rmaOrderItems={rmaOrderItems}
                      order={order}
                      onChange={onProductSelectorChange}
                    />
                  ))}

            <FormGroup label={t("inputs.subject.label")} labelInfo="*">
              <HTMLSelect
                onChange={this.handleChange("reason")}
                value={reason}
                fill={true}
              >
                <option value="" disabled={true}>
                  {t("inputs.subject.placeholder")}
                </option>
                {topics.map(([key, value]) => (
                  <option key={key} value={key} children={value} />
                ))}
              </HTMLSelect>
            </FormGroup>

            <FormGroup
              label={t("inputs.fileInput.label")}
              labelInfo={fileUploadRequired ? "*" : ""}
            >
              <label className="bp3-file-input bp3-fill">
                <input
                  type="file"
                  onChange={this.handleDocumentChange("document")}
                />
                <span className="bp3-file-upload-input">
                  {document ? document.name : t("inputs.fileInput.placeholder")}
                </span>
              </label>
            </FormGroup>

            <FormGroup label={t("inputs.description.label")} labelInfo={"*"}>
              <TextArea
                fill={true}
                onChange={this.handleChange("description")}
                value={description}
              />
            </FormGroup>

            <RightAligned>
              <Button
                loading={loading}
                type="submit"
                intent="primary"
                large={true}
              >
                {t("actions.submit")}
              </Button>
            </RightAligned>
          </form>
        </Card>
      </>
    );
  }

  public renderError() {
    const { errorCode, t } = this.props;

    switch (errorCode) {
      case "requiredQuantity":
        return (
          <Callout intent="danger" title={t("errors.requiredQuantity.title")}>
            <div>{t("errors.requiredQuantity.description")}</div>
          </Callout>
        );

      case "networkError":
        return (
          <Callout intent="danger">
            <div>{t("errors.networkError")}</div>
          </Callout>
        );

      case "enterRequiredFields":
        return (
          <Callout
            intent="danger"
            title={t("errors.enterRequiredFields.title")}
          >
            <div>{t("errors.enterRequiredFields.description")}</div>
          </Callout>
        );

      default:
        return null;
    }
  }

  public orderNumberValidationStateProps() {
    switch (this.props.orderNumberValidationState) {
      case OrderNumberValidationState.DEFAULT:
        return {};

      case OrderNumberValidationState.LOADING:
        return { rightElement: <Spinner size={16} /> };

      case OrderNumberValidationState.VALID:
        return { rightIcon: <Icon icon={IconNames.TICK} intent="success" /> };

      case OrderNumberValidationState.INVALID:
        return { rightIcon: <Icon icon={IconNames.CROSS} intent="danger" /> };

      default:
        return {};
    }
  }

  public handleOrderNumberBlur = () => {
    this.props.onValidateOrderNumber();
  };

  public handleChange = (field: string) => (
    event:
      | React.FormEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    this.props.onChange({
      [field]: event.currentTarget.value
    });
  };

  public handleDocumentChange = (field: string) => (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];

    if (!file) {
      return;
    }

    this.props.onChange({ [field]: file });
  };

  public handlePostalCodeChange = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    this.props.onPostalCodeChange(event.currentTarget.value);
  };

  public handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    this.props.onSubmit(event);
  };
}

export default withTranslation("rmaForm")(RMAForm);



// WEBPACK FOOTER //
// ./src/components/RMAForm/RMAForm.tsx